<template>
  <div
    class="tc-verify-row tc-verify-row--draft"
    :class="{
      'tc-verify-row--editing': editing,
      'tc-verify-row--invalid': !!error,
      'tc-verify-row--conflict': vpStatus === 'overlap',
    }"
    :data-testid="`verify-row-${draft.eventId}`"
  >
    <ConfidenceBar state="confirmed" variant="bar" class="tc-verify-row-edge" />

    <div class="tc-verify-row-day">
      <div class="tc-verify-row-day-label">{{ dayLabel }}</div>
    </div>

    <div class="tc-verify-row-wbs">
      <div class="tc-verify-row-wbs-top">
        <span class="tc-verify-row-wbs-code">{{ draft.wbs1 }}</span>
        <span class="tc-verify-chip tc-verify-chip--new">New</span>
        <span
          v-if="vpStatus === 'overlap'"
          class="tc-verify-chip tc-verify-chip--conflict"
          data-testid="verify-row-conflict-chip"
        >Adds to {{ (conflictVpHours ?? 0).toFixed(2) }}h in VP</span>
      </div>
      <div class="tc-verify-row-wbs-name">{{ draft.wbs1Name || '(unnamed project)' }}</div>
      <div v-if="draft.comment && !editing && !commentRequired" class="tc-verify-row-comment">{{ draft.comment }}</div>
    </div>

    <template v-if="!editing">
      <div class="tc-verify-row-hours" data-testid="verify-row-hours">
        {{ (draft.hours ?? 0).toFixed(2) }}h
      </div>
      <div></div>
      <div class="tc-verify-row-actions">
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :data-testid="`verify-row-edit-${draft.eventId}`"
          @click="startEditing"
        >Edit</button>
        <button
          type="button"
          class="btn btn-outline-danger btn-sm"
          :data-testid="`verify-row-remove-${draft.eventId}`"
          @click="$emit('remove', draft.eventId)"
        >Remove</button>
      </div>
    </template>

    <template v-else>
      <div class="tc-verify-row-hours">
        <input
          type="number"
          :step="hoursIncrement"
          min="0"
          max="24"
          class="form-control form-control-sm"
          :data-testid="`verify-row-hours-input-${draft.eventId}`"
          v-model.number="hoursDraft"
        />
      </div>
      <div></div>
      <div class="tc-verify-row-actions">
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :data-testid="`verify-row-cancel-${draft.eventId}`"
          @click="cancelEditing"
        >Cancel</button>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          :data-testid="`verify-row-confirm-${draft.eventId}`"
          @click="confirmEditing"
        >Save</button>
      </div>
      <div class="tc-verify-row-edit-fields">
        <input
          type="text"
          class="form-control form-control-sm"
          placeholder="Comment (optional)"
          :data-testid="`verify-row-comment-input-${draft.eventId}`"
          v-model="commentDraft"
        />
      </div>
    </template>

    <!-- Required-note field: always visible when the tenant requires a note, so
         it can't vanish mid-type; flagged invalid while empty (the submit gate
         + banner count the empties). -->
    <!-- Labor code select: shown when the tenant enables it (showLaborCode=Y). -->
    <div
      v-if="showLaborCode && laborCodes.length > 0 && !editing"
      class="tc-verify-row-edit-fields"
      data-testid="verify-row-labor-code-field"
    >
      <select
        class="form-select form-select-sm"
        :data-testid="`verify-row-labor-code-${draft.eventId}`"
        :value="laborCodeDraft"
        :disabled="!canEditLaborCode"
        :title="canEditLaborCode ? undefined : 'Set by Vantagepoint — not editable here'"
        @change="onLaborCodeChange"
      >
        <option value="">No labor code</option>
        <option
          v-for="lc in laborCodes"
          :key="lc.code"
          :value="lc.code"
        >{{ lc.label }} ({{ lc.code }})</option>
      </select>
    </div>

    <div v-if="commentRequired && !editing" class="tc-verify-row-edit-fields" data-testid="verify-row-note-required">
      <input
        type="text"
        class="form-control form-control-sm"
        :class="{ 'is-invalid': noteMissing }"
        placeholder="Add a comment (required by Vantagepoint)"
        :data-testid="`verify-row-note-input-${draft.eventId}`"
        v-model="noteDraft"
        @input="onNoteInput"
      />
    </div>

    <div v-if="error" class="tc-verify-row-error" role="alert">{{ error }}</div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type { TriageDraft } from '../stores/triage';
import type { LaborCodeItem } from '../api/timesheetSettings';
import ConfidenceBar from './ui/ConfidenceBar.vue';
import { formatMonthDay } from '../utils/period';

const props = defineProps<{
  draft: TriageDraft;
  /** ISO YYYY-MM-DD of the period start (dayIndex=0). */
  periodStart: string;
  /** Inline error string for this row (e.g. validation feedback from a 400 response). */
  error?: string | null;
  /** Reconcile state vs live VP under always-append: a brand-'new' cell, or an
   *  'overlap' (a row will be appended onto a project+day that already has VP
   *  hours → VP sums). 'in-vp' cells (exact tuple+day+hours match) are deduped
   *  before render and never reach this row. */
  vpStatus?: 'new' | 'in-vp' | 'overlap';
  /** Existing VP hours at this cell's (project, day) — shown on the conflict
   *  chip ("Adds to N.Nh in VP") when vpStatus is 'overlap'. */
  conflictVpHours?: number;
  /** Tenant requires a note on every entry (VP "Comment required"). When set,
   *  the row shows an always-visible note field, flagged invalid while empty. */
  commentRequired?: boolean;
  /** Hours step value from tenant settings (VP increment code). Defaults to 0.25. */
  hoursIncrement?: number;
  /** When true, show the labor code selector. */
  showLaborCode?: boolean;
  /** When false, the labor code is locked to the default (VP ChangeDefaultLC !== 'Y'). */
  canEditLaborCode?: boolean;
  /** Available labor codes for this tenant. */
  laborCodes?: LaborCodeItem[];
  /** Tenant default labor code (pre-selects the dropdown). */
  defaultLaborCode?: string;
}>();

const emit = defineEmits<{
  update: [eventId: string, patch: { hours: number; comment: string; laborCode?: string }];
  remove: [eventId: string];
}>();

const editing = ref(false);
const hoursDraft = ref(props.draft.hours ?? 0);
const commentDraft = ref(props.draft.comment ?? '');

const hoursIncrement = computed(() => props.hoursIncrement ?? 0.25);
const laborCodes = computed(() => props.laborCodes ?? []);
const showLaborCode = computed(() => !!props.showLaborCode && laborCodes.value.length > 0);
// Default to editable when the prop is absent (fixtures/older callers) so the
// only lock is an explicit false from the VP-config-driven parent.
const canEditLaborCode = computed(() => props.canEditLaborCode !== false);

// Labor code: use the entry's existing value, then the tenant default, then empty.
const laborCodeDraft = ref(props.draft.laborCode ?? props.defaultLaborCode ?? '');
watch(() => props.draft.laborCode, (v) => { laborCodeDraft.value = v ?? props.defaultLaborCode ?? ''; });

function onLaborCodeChange(e: Event): void {
  laborCodeDraft.value = (e.target as HTMLSelectElement).value;
  emit('update', props.draft.eventId, {
    hours: props.draft.hours ?? 0,
    comment: props.draft.comment ?? '',
    laborCode: laborCodeDraft.value || undefined,
  });
}

// Inline note field shown when a note is required. Kept in its own ref so live
// edits don't re-key the row; synced if the comment changes from elsewhere.
const noteDraft = ref(props.draft.comment ?? '');
watch(() => props.draft.comment, (v) => { if ((v ?? '') !== noteDraft.value) noteDraft.value = v ?? ''; });
const noteMissing = computed(() => !!props.commentRequired && !noteDraft.value.trim());

function onNoteInput() {
  emit('update', props.draft.eventId, { hours: props.draft.hours ?? 0, comment: noteDraft.value });
}

const SHORT_DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const dayLabel = computed(() => {
  const dayIndex = props.draft.dayIndex ?? 0;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(props.periodStart);
  if (!m) return `Day ${dayIndex + 1}`;
  const base = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  base.setUTCDate(base.getUTCDate() + dayIndex);
  return `${SHORT_DOW[base.getUTCDay()]} ${formatMonthDay(base.toISOString().slice(0, 10))}`;
});

function startEditing() {
  hoursDraft.value = props.draft.hours ?? 0;
  commentDraft.value = props.draft.comment ?? '';
  editing.value = true;
}

function cancelEditing() {
  editing.value = false;
}

function confirmEditing() {
  emit('update', props.draft.eventId, {
    hours: Number.isFinite(hoursDraft.value) ? hoursDraft.value : 0,
    comment: commentDraft.value,
    laborCode: laborCodeDraft.value || undefined,
  });
  editing.value = false;
}
</script>
